/**
 * Route guards as a mechanism (PRD §8 gap table). `flows.md`'s Guard column is
 * documentation; nothing enforced it in the prototype. This is that enforcement — **UX
 * only**, same as every client-side check in this shell: the server's `403`/`409` remains
 * the sole authority (contracts.md §10).
 *
 * P7 supplies per-route policy (e.g. "Manage rows only for a gate's manager") by adding more
 * guards through `composeGuards`; this file supplies only the mechanism plus the one guard
 * that is the shell's own boot-sequence concern — whether a session exists at all.
 */
import type { AuthStatus } from './authStatus'
import { AUTH_ROUTE, HOME_ROUTE, type Route } from './routes'

/**
 * Returns `true` to allow the route, or a replacement `Route` to redirect to instead.
 * Never throws — an unmet guard is a redirect, not an error state.
 */
export type RouteGuard = (route: Route, auth: AuthStatus) => true | Route

export function composeGuards(guards: readonly RouteGuard[]): RouteGuard {
  return (route, auth) => {
    for (const guard of guards) {
      const result = guard(route, auth)
      if (result !== true) return result
    }
    return true
  }
}

/**
 * §13 "reuses its session record rather than consuming an additional slot" / §7.9's first-sign-up capture: an unauthenticated viewer may only be on `auth`,
 * and a signed-in viewer with an uncaptured name (D42) is held there too, until
 * `AuthStatusContext` reports `pendingName: false`. Every other route requires a session —
 * and the reverse holds too: a fully signed-in viewer is bounced off `auth` to Home, because
 * nothing else ever calls `navigate()` away from it (sign-in completing is a status change,
 * not a link click).
 *
 * `auth.state === 'loading'` (the boot-time silent-refresh window, §7.11) is not this
 * guard's concern — `Router` checks it before consulting any guard and renders a boot state
 * instead of a route, so a guard never has to invent a fourth outcome.
 */
export const requireAuth: RouteGuard = (route, auth) => {
  if (auth.state === 'loading') return true
  // **Restoring is undecided, and the one route it may not show is the OTP screen** — that is the
  // 2026-09-25 defect: a resident with a session still on the phone, put on sign-in because a read
  // rejected. Every other route stands, as under `loading`; their calls wait at `request()`.
  if (auth.state === 'restoring') return route.id === 'auth' ? HOME_ROUTE : true
  if (auth.state === 'signed-out') return route.id === 'auth' ? true : AUTH_ROUTE
  if (auth.pendingName) return route.id === 'auth' ? true : AUTH_ROUTE
  if (route.id === 'auth') return HOME_ROUTE
  return true
}
